import { useCallback, useEffect, useMemo, useRef } from 'react'
import { isEmpty } from 'lodash'
import { useOnInView } from 'react-intersection-observer'
import CollapsibleGroupComponent from '../../../../components/stories/molecules/collapsible-group'
import { useHistory, useParams } from 'react-router-dom'
import colorsOptions from '../../../../components/stories/fundamentals/colors-styles/constants'
import { planMenuItemTypeOptions } from '../../constants'
import {
  Folder,
  Plan
} from '../../../../components/stories/atoms/icons/entities'
import { SpinnerGrey } from '../../../../components/stories/atoms/icons/special'
import { iconSizeOptions } from '../../../../components/stories/atoms/icons/icons-sizes'
import {
  planTreeSortByOptions,
  planTreeSortDirectionOptions,
  useGetPlanTreeQuery
} from '../../../../react-query/plan/query/use-get-plan-tree-query'
import { EmptyFolderLabel, LoadingNextPage } from '../../styled-component'
import { FormattedMessage } from 'react-intl'
import PlanMenuMobilePlanDescription from './plan-menu-mobile-plan-description'
import PlanMenuMobilePlanListLoad from './plan-menu-mobile-plan-list-load'
import PlanMenuMobileEmptyList from './plan-menu-mobile-empty-list'
import PropTypes from 'prop-types'

const PlanMenuMobilePlanList = ({
  folderId,
  search = '',
  isLoading = false,
  isChildren = false,
  onEmptyStateChange
}) => {
  const { projectId } = useParams()
  const history = useHistory()

  const queryParams = useMemo(
    () => ({
      search,
      sortDirection: planTreeSortDirectionOptions.DESC,
      sortBy: planTreeSortByOptions.LAST_ACTIVITY
    }),
    [search]
  )

  // queries
  const {
    data: planMenuList,
    isLoading: planMenuListLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetPlanTreeQuery({
    projectId,
    folderId,
    params: queryParams
  })

  const planMenuTree = planMenuList?.pages.flatMap((page) => page.content) ?? []

  const hasPlans =
    !planMenuListLoading &&
    !isLoading &&
    planMenuTree.length > 0 &&
    isEmpty(search)

  useEffect(() => {
    if (isChildren) return
    onEmptyStateChange?.(!hasPlans)
  }, [isChildren, hasPlans, onEmptyStateChange])

  // pagination
  const onBottomReached = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return
    fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const onBottomReachedRef = useRef(onBottomReached)

  useEffect(() => {
    onBottomReachedRef.current = onBottomReached
  }, [onBottomReached])

  const bottomReachedRef = useOnInView(
    useCallback((inView) => {
      if (inView) onBottomReachedRef.current?.()
    }, []),
    { rootMargin: '20px', skip: !hasNextPage }
  )

  // helpers

  const openPlan = (item) => {
    history.push(`/project/${projectId}/${item.id}/plan`)
  }

  const getIcon = (itemType) => {
    const IconComponent =
      itemType === planMenuItemTypeOptions.FOLDER ? Folder : Plan

    return (
      <IconComponent
        size={iconSizeOptions['ICON-L']}
        style={{ marginRight: '0.5rem' }}
      />
    )
  }

  // renders
  if (planMenuListLoading || isLoading)
    return <PlanMenuMobilePlanListLoad isChildren={isChildren} />

  if (planMenuTree.length === 0 && !isChildren)
    return <PlanMenuMobileEmptyList search={search} />

  return (
    <CollapsibleGroupComponent.Root
      style={{
        backgroundColor: colorsOptions.greys.white,
        borderRadius: !isChildren ? '16px' : '0px',
        flexShrink: 0
      }}
    >
      {planMenuTree.map((item, index) => {
        const isFolder = item.type === planMenuItemTypeOptions.FOLDER
        const icon = getIcon(item.type)
        const isEmptyChildren = item.items === 0
        const isLastItem = index === planMenuTree.length - 1
        // folders keep the item's own expand/collapse toggle; only plans navigate on click
        const openOnClick = !isFolder ? { onClick: () => openPlan(item) } : {}

        return (
          <CollapsibleGroupComponent.Item
            key={item.id}
            leftIcon={icon}
            title={item?.name}
            titleStyle={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            description={<PlanMenuMobilePlanDescription item={item} />}
            showChevronRight={isFolder}
            showChevronLeft={false}
            borderBottom={!isLastItem}
            borderFull={false}
            style={{ padding: '0.5rem' }}
            {...openOnClick}
          >
            {isFolder && isEmptyChildren && (
              <EmptyFolderLabel>
                <FormattedMessage id="empty_folder" />
              </EmptyFolderLabel>
            )}
            {isFolder && !isEmptyChildren && (
              <PlanMenuMobilePlanList folderId={item?.id} isChildren />
            )}
          </CollapsibleGroupComponent.Item>
        )
      })}

      {isFetchingNextPage && (
        <LoadingNextPage>
          <SpinnerGrey size={iconSizeOptions['ICON-XL']} />
        </LoadingNextPage>
      )}

      <div ref={bottomReachedRef} style={{ height: '1px' }} />
    </CollapsibleGroupComponent.Root>
  )
}

PlanMenuMobilePlanList.propTypes = {
  folderId: PropTypes.string,
  search: PropTypes.string,
  isLoading: PropTypes.bool,
  isChildren: PropTypes.bool,
  onEmptyStateChange: PropTypes.func
}

export default PlanMenuMobilePlanList
